import { supabase } from "../lib/supabase";

export const NOVA_MEDIA_DRAFT_BUCKET =
  "nova-media-drafts";

export const NOVA_MEDIA_PUBLIC_BUCKET =
  "nova-media-public";

const NOVA_MENU_IMAGE_MAX_BYTES =
  8 * 1024 * 1024;

/*
 * MENU PHOTOGRAPHY ONLY
 *
 * Whatever normal menu photo the CMS user selects is
 * centre-cropped and optimized in the browser before it
 * reaches Supabase Storage.
 *
 * Other Nova media uploads are not affected.
 */
const NOVA_MENU_IMAGE_SIZE =
  800;

const NOVA_MENU_IMAGE_QUALITY =
  0.82;

const NOVA_MENU_IMAGE_TYPES =
  new Set([
    "image/jpeg",
    "image/png",
    "image/webp",
  ]);

async function optimizeNovaMenuImage(
  file: File,
): Promise<Blob> {
  let bitmap: ImageBitmap;

  try {
    bitmap =
      await createImageBitmap(
        file,
        {
          imageOrientation:
            "from-image",
        },
      );
  } catch {
    bitmap =
      await createImageBitmap(
        file,
      );
  }

  try {
    const canvas =
      document.createElement(
        "canvas",
      );

    canvas.width =
      NOVA_MENU_IMAGE_SIZE;

    canvas.height =
      NOVA_MENU_IMAGE_SIZE;

    const context =
      canvas.getContext(
        "2d",
      );

    if (!context) {
      throw new Error(
        "This browser could not prepare the menu photograph.",
      );
    }

    /*
     * MENU IMAGE STANDARD
     *
     * Preserve the entire photograph instead of cropping it.
     * This prevents plates, bowls and food at the edges from
     * being cut off in the published menu image.
     */
    const scale =
      Math.min(
        NOVA_MENU_IMAGE_SIZE /
          bitmap.width,
        NOVA_MENU_IMAGE_SIZE /
          bitmap.height,
      );

    const drawWidth =
      Math.max(
        1,
        Math.round(
          bitmap.width *
            scale,
        ),
      );

    const drawHeight =
      Math.max(
        1,
        Math.round(
          bitmap.height *
            scale,
        ),
      );

    const drawX =
      Math.round(
        (
          NOVA_MENU_IMAGE_SIZE -
          drawWidth
        ) / 2,
      );

    const drawY =
      Math.round(
        (
          NOVA_MENU_IMAGE_SIZE -
          drawHeight
        ) / 2,
      );

    /*
     * Neutral restaurant-toned background is only visible
     * when the original photograph is not square.
     */
    context.fillStyle =
      "#e8e2d5";

    context.fillRect(
      0,
      0,
      NOVA_MENU_IMAGE_SIZE,
      NOVA_MENU_IMAGE_SIZE,
    );

    context.imageSmoothingEnabled =
      true;

    context.imageSmoothingQuality =
      "high";

    context.drawImage(
      bitmap,
      0,
      0,
      bitmap.width,
      bitmap.height,
      drawX,
      drawY,
      drawWidth,
      drawHeight,
    );

    const blob =
      await new Promise<Blob | null>(
        (resolve) =>
          canvas.toBlob(
            resolve,
            "image/webp",
            NOVA_MENU_IMAGE_QUALITY,
          ),
      );

    if (!blob) {
      throw new Error(
        "This browser could not optimize the menu photograph.",
      );
    }

    return blob;
  } finally {
    bitmap.close();
  }
}

function safeMenuImageFileName(
  value: string,
) {
  const clean =
    value
      .trim()
      .toLowerCase()
      .replace(
        /[^a-z0-9._-]+/g,
        "-",
      )
      .replace(
        /-+/g,
        "-",
      )
      .replace(
        /^[-.]+|[-.]+$/g,
        "",
      );

  return (
    clean ||
    "menu-photo.jpg"
  );
}

export async function uploadNovaMenuItemImage(
  itemId: string,
  file: File,
): Promise<string> {
  if (
    !NOVA_MENU_IMAGE_TYPES.has(
      file.type,
    )
  ) {
    throw new Error(
      "Menu photography must be a JPG, PNG or WebP image.",
    );
  }

  if (
    file.size >
    NOVA_MENU_IMAGE_MAX_BYTES
  ) {
    throw new Error(
      "Menu photography must be 8 MB or smaller.",
    );
  }

  const cleanItemId =
    itemId
      .trim()
      .toLowerCase()
      .replace(
        /[^a-z0-9_-]+/g,
        "-",
      )
      .replace(
        /-+/g,
        "-",
      )
      .replace(
        /^-+|-+$/g,
        "",
      );

  if (!cleanItemId) {
    throw new Error(
      "Save a valid menu item name before uploading photography.",
    );
  }

  const optimizedImage =
    await optimizeNovaMenuImage(
      file,
    );

  const originalName =
    safeMenuImageFileName(
      file.name,
    );

  const baseName =
    originalName.replace(
      /\.[^.]+$/,
      "",
    ) ||
    "menu-photo";

  const path =
    `menu-items/${cleanItemId}/` +
    `${Date.now()}-` +
    `${crypto.randomUUID()}-` +
    `${baseName}.webp`;

  const {
    error: uploadError,
  } =
    await supabase.storage
      .from(
        NOVA_MEDIA_PUBLIC_BUCKET,
      )
      .upload(
        path,
        optimizedImage,
        {
          contentType:
            "image/webp",
          upsert: false,
          cacheControl:
            "31536000",
        },
      );

  if (uploadError) {
    throw new Error(
      uploadError.message ||
        "Menu photography upload failed.",
    );
  }

  return publicUrl(
    path,
  );
}

export type NovaPublicMedia = {
  heroBackground: string;
  tableImage: string;
  juiceImage: string;

  logoLight: string;
  logoDark: string;
  logoFallback: string;

  atNovaFeature: string | null;
  atNovaGameNights: string | null;
  atNovaSetups: string | null;
  atNovaKitchen: string | null;
  atNovaMoments: string | null;
};

export const DEFAULT_NOVA_MEDIA: NovaPublicMedia = {
  // CMS-managed photography must never fall back to obsolete placeholder
  // photographs while published media is loading.
  heroBackground: "",

  tableImage: "",

  juiceImage: "",

  logoLight:
    "/images/nova/logo2.png",

  logoDark:
    "/images/nova/logodark.png",

  logoFallback:
    "/images/nova/novatransparent.png",

  atNovaFeature: null,
  atNovaGameNights: null,
  atNovaSetups: null,
  atNovaKitchen: null,
  atNovaMoments: null,
};

const SLOT_TO_PUBLIC_KEY: Record<
  string,
  keyof NovaPublicMedia
> = {
  "hero.background":
    "heroBackground",

  "table.image":
    "tableImage",

  "juice.image":
    "juiceImage",

  "brand.logo_light":
    "logoLight",

  "brand.logo_dark":
    "logoDark",

  "brand.logo_fallback":
    "logoFallback",

  "at_nova.feature":
    "atNovaFeature",

  "at_nova.game_nights":
    "atNovaGameNights",

  "at_nova.setups":
    "atNovaSetups",

  "at_nova.kitchen":
    "atNovaKitchen",

  "at_nova.moments":
    "atNovaMoments",
};

type PublicMediaRow = {
  slot_key: string;
  media_kind: "image" | "video";
  published_path: string | null;
  published_at: string | null;
};

export type NovaMediaSlot = {
  slot_key: string;
  section_key: string;
  label: string;
  media_kind: "image" | "video";

  draft_path: string | null;
  draft_file_name: string | null;
  draft_mime_type: string | null;
  draft_updated_at: string | null;

  published_path: string | null;
  published_file_name: string | null;
  published_mime_type: string | null;
  published_at: string | null;

  draft_url: string | null;
  published_url: string | null;
};

function publicUrl(
  path: string,
) {
  return supabase.storage
    .from(
      NOVA_MEDIA_PUBLIC_BUCKET,
    )
    .getPublicUrl(path)
    .data.publicUrl;
}

async function removeStorageObject(
  bucket: string,
  path: string | null,
) {
  if (!path) {
    return;
  }

  const { error } =
    await supabase.storage
      .from(bucket)
      .remove([path]);

  if (error) {
    throw new Error(
      error.message ||
        `Could not remove ${path}.`,
    );
  }
}

export async function loadPublishedNovaMedia(): Promise<NovaPublicMedia> {
  const media: NovaPublicMedia = {
    ...DEFAULT_NOVA_MEDIA,
  };

  const { data, error } =
    await supabase.rpc(
      "get_nova_public_media",
    );

  if (error) {
    console.error(
      "[Nova Media] public media load failed",
      error,
    );

    return media;
  }

  for (
    const row of (
      Array.isArray(data)
        ? data
        : []
    ) as PublicMediaRow[]
  ) {
    if (!row.published_path) {
      continue;
    }

    const key =
      SLOT_TO_PUBLIC_KEY[
        row.slot_key
      ];

    if (!key) {
      continue;
    }

    media[key] =
      publicUrl(
        row.published_path,
      );
  }

  return media;
}

export async function loadNovaMediaSlots(): Promise<
  NovaMediaSlot[]
> {
  const { data, error } =
    await supabase.rpc(
      "get_nova_media_slots",
    );

  if (error) {
    throw new Error(
      error.message ||
        "Nova media could not be loaded.",
    );
  }

  const rows =
    (Array.isArray(data)
      ? data
      : []) as Array<
      Omit<
        NovaMediaSlot,
        | "draft_url"
        | "published_url"
      >
    >;

  return Promise.all(
    rows.map(
      async (
        row,
      ): Promise<NovaMediaSlot> => {
        let draftUrl:
          | string
          | null = null;

        if (row.draft_path) {
          const {
            data:
              signedData,
            error:
              signedError,
          } =
            await supabase.storage
              .from(
                NOVA_MEDIA_DRAFT_BUCKET,
              )
              .createSignedUrl(
                row.draft_path,
                60 * 60,
              );

          if (!signedError) {
            draftUrl =
              signedData
                ?.signedUrl ??
              null;
          }
        }

        return {
          ...row,

          draft_url:
            draftUrl,

          published_url:
            row.published_path
              ? publicUrl(
                  row.published_path,
                )
              : null,
        };
      },
    ),
  );
}

function safeFileName(
  value: string,
) {
  const cleaned =
    value
      .trim()
      .toLowerCase()
      .replace(
        /[^a-z0-9._-]+/g,
        "-",
      )
      .replace(
        /-+/g,
        "-",
      )
      .replace(
        /^[-.]+|[-.]+$/g,
        "",
      );

  return (
    cleaned ||
    "nova-media"
  );
}

function validateMediaFile(
  slot: NovaMediaSlot,
  file: File,
) {
  if (
    slot.media_kind ===
      "image" &&
    !file.type.startsWith(
      "image/",
    )
  ) {
    throw new Error(
      "This media slot accepts images only.",
    );
  }

  if (
    slot.media_kind ===
      "video" &&
    ![
      "video/mp4",
      "video/webm",
    ].includes(
      file.type,
    )
  ) {
    throw new Error(
      "This media slot accepts MP4 or WEBM video.",
    );
  }

  if (
    file.size >
    50 * 1024 * 1024
  ) {
    throw new Error(
      "Media must be 50 MB or smaller.",
    );
  }
}

async function clearExistingDraft(
  slotKey: string,
) {
  const {
    data,
    error,
  } =
    await supabase.rpc(
      "take_nova_media_draft_path",
      {
        p_slot_key:
          slotKey,
      },
    );

  if (error) {
    throw new Error(
      error.message ||
        "Existing draft could not be cleared.",
    );
  }

  const oldPath =
    typeof data === "string"
      ? data
      : null;

  if (oldPath) {
    await removeStorageObject(
      NOVA_MEDIA_DRAFT_BUCKET,
      oldPath,
    );
  }
}

export async function uploadNovaMediaDraft(
  slot: NovaMediaSlot,
  file: File,
): Promise<void> {
  validateMediaFile(
    slot,
    file,
  );

  /*
   * Important:
   * Upload and stage the NEW file before deleting the old draft.
   *
   * This means a failed network upload never destroys the
   * draft that was already safely stored.
   */
  const oldDraftPath =
    slot.draft_path;

  const fileName =
    safeFileName(file.name);

  const path =
    `${slot.slot_key}/` +
    `${Date.now()}-` +
    `${crypto.randomUUID()}-` +
    fileName;

  const {
    error: uploadError,
  } =
    await supabase.storage
      .from(
        NOVA_MEDIA_DRAFT_BUCKET,
      )
      .upload(
        path,
        file,
        {
          contentType:
            file.type,
          upsert: false,
        },
      );

  if (uploadError) {
    throw new Error(
      uploadError.message ||
        "Media upload failed.",
    );
  }

  const {
    error: stageError,
  } =
    await supabase.rpc(
      "stage_nova_media",
      {
        p_slot_key:
          slot.slot_key,

        p_draft_path:
          path,

        p_file_name:
          file.name,

        p_mime_type:
          file.type,
      },
    );

  if (stageError) {
    await removeStorageObject(
      NOVA_MEDIA_DRAFT_BUCKET,
      path,
    );

    throw new Error(
      stageError.message ||
        "Media draft could not be staged.",
    );
  }

  /*
   * The DB now safely references the new draft.
   * Only now may the superseded draft be removed.
   *
   * Cleanup failure must not pretend the upload failed,
   * because the NEW draft is already valid and staged.
   */
  if (
    oldDraftPath &&
    oldDraftPath !== path
  ) {
    try {
      await removeStorageObject(
        NOVA_MEDIA_DRAFT_BUCKET,
        oldDraftPath,
      );
    } catch (
      cleanupError
    ) {
      console.warn(
        "[Nova Media] old draft cleanup deferred",
        cleanupError,
      );
    }
  }
}

export async function publishNovaMediaSlot(
  slot: NovaMediaSlot,
): Promise<void> {
  if (!slot.draft_path) {
    throw new Error(
      "Upload a draft before publishing.",
    );
  }

  const oldPublishedPath =
    slot.published_path;

  const {
    data: draftFile,
    error: downloadError,
  } =
    await supabase.storage
      .from(
        NOVA_MEDIA_DRAFT_BUCKET,
      )
      .download(
        slot.draft_path,
      );

  if (
    downloadError ||
    !draftFile
  ) {
    throw new Error(
      downloadError?.message ||
        "Draft media could not be read.",
    );
  }

  const fileName =
    safeFileName(
      slot.draft_file_name ||
        "nova-media",
    );

  const publicPath =
    `${slot.slot_key}/` +
    `${Date.now()}-` +
    `${crypto.randomUUID()}-` +
    fileName;

  const {
    error:
      publicUploadError,
  } =
    await supabase.storage
      .from(
        NOVA_MEDIA_PUBLIC_BUCKET,
      )
      .upload(
        publicPath,
        draftFile,
        {
          contentType:
            slot.draft_mime_type ??
            draftFile.type,

          upsert: false,
        },
      );

  if (
    publicUploadError
  ) {
    throw new Error(
      publicUploadError.message ||
        "Published media upload failed.",
    );
  }

  const {
    error:
      publishError,
  } =
    await supabase.rpc(
      "publish_nova_media",
      {
        p_slot_key:
          slot.slot_key,

        p_public_path:
          publicPath,

        p_file_name:
          slot.draft_file_name ??
          fileName,

        p_mime_type:
          slot.draft_mime_type ??
          draftFile.type,
      },
    );

  if (publishError) {
    await removeStorageObject(
      NOVA_MEDIA_PUBLIC_BUCKET,
      publicPath,
    );

    throw new Error(
      publishError.message ||
        "Media could not be published.",
    );
  }

  await removeStorageObject(
    NOVA_MEDIA_DRAFT_BUCKET,
    slot.draft_path,
  );

  if (
    oldPublishedPath &&
    oldPublishedPath !==
      publicPath
  ) {
    await removeStorageObject(
      NOVA_MEDIA_PUBLIC_BUCKET,
      oldPublishedPath,
    );
  }
}

export async function discardNovaMediaDraft(
  slot: NovaMediaSlot,
): Promise<void> {
  const {
    data,
    error,
  } =
    await supabase.rpc(
      "take_nova_media_draft_path",
      {
        p_slot_key:
          slot.slot_key,
      },
    );

  if (error) {
    throw new Error(
      error.message ||
        "Draft could not be discarded.",
    );
  }

  const oldPath =
    typeof data === "string"
      ? data
      : slot.draft_path;

  await removeStorageObject(
    NOVA_MEDIA_DRAFT_BUCKET,
    oldPath,
  );
}

export async function removeNovaPublishedMedia(
  slot: NovaMediaSlot,
): Promise<void> {
  const {
    data,
    error,
  } =
    await supabase.rpc(
      "remove_nova_published_media",
      {
        p_slot_key:
          slot.slot_key,
      },
    );

  if (error) {
    throw new Error(
      error.message ||
        "Published media could not be removed.",
    );
  }

  const oldPath =
    typeof data === "string"
      ? data
      : slot.published_path;

  await removeStorageObject(
    NOVA_MEDIA_PUBLIC_BUCKET,
    oldPath,
  );
}


export type NovaMediaCleanupResult = {
  draftFilesRemoved: number;
  publishedFilesRemoved: number;
  totalRemoved: number;
};

async function findOrphanFiles(
  bucket: string,
  slot: NovaMediaSlot,
  keepPath: string | null,
): Promise<string[]> {
  const {
    data,
    error,
  } =
    await supabase.storage
      .from(bucket)
      .list(
        slot.slot_key,
        {
          limit: 100,
          sortBy: {
            column: "name",
            order: "asc",
          },
        },
      );

  if (error) {
    throw new Error(
      error.message ||
        `Could not inspect ${slot.label}.`,
    );
  }

  const keep =
    keepPath
      ? new Set([
          keepPath,
        ])
      : new Set<string>();

  return (
    data ?? []
  )
    .filter(
      (entry) =>
        Boolean(entry.id),
    )
    .map(
      (entry) =>
        `${slot.slot_key}/${entry.name}`,
    )
    .filter(
      (path) =>
        !keep.has(path),
    );
}

export async function cleanupNovaMediaOrphans(
  slots: NovaMediaSlot[],
): Promise<NovaMediaCleanupResult> {
  let draftFilesRemoved = 0;
  let publishedFilesRemoved = 0;

  for (
    const slot of slots
  ) {
    const draftOrphans =
      await findOrphanFiles(
        NOVA_MEDIA_DRAFT_BUCKET,
        slot,
        slot.draft_path,
      );

    if (
      draftOrphans.length >
      0
    ) {
      const { error } =
        await supabase.storage
          .from(
            NOVA_MEDIA_DRAFT_BUCKET,
          )
          .remove(
            draftOrphans,
          );

      if (error) {
        throw new Error(
          error.message ||
            `Unused drafts could not be removed from ${slot.label}.`,
        );
      }

      draftFilesRemoved +=
        draftOrphans.length;
    }

    const publishedOrphans =
      await findOrphanFiles(
        NOVA_MEDIA_PUBLIC_BUCKET,
        slot,
        slot.published_path,
      );

    if (
      publishedOrphans.length >
      0
    ) {
      const { error } =
        await supabase.storage
          .from(
            NOVA_MEDIA_PUBLIC_BUCKET,
          )
          .remove(
            publishedOrphans,
          );

      if (error) {
        throw new Error(
          error.message ||
            `Unused published media could not be removed from ${slot.label}.`,
        );
      }

      publishedFilesRemoved +=
        publishedOrphans.length;
    }
  }

  return {
    draftFilesRemoved,
    publishedFilesRemoved,

    totalRemoved:
      draftFilesRemoved +
      publishedFilesRemoved,
  };
}
